import React, { useEffect, useMemo, useState } from "react";
import { Container, Button, Spinner, Alert, Card } from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminXPosts.css";

const API_BASE = process.env.REACT_APP_API_BASE;

async function authHeader() {
  const session = await Auth.currentSession();
  const token = session.getAccessToken().getJwtToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function fmtDate(ms) {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  return Number.isNaN(d.getTime()) ? String(ms) : d.toLocaleString();
}

export default function AdminXPosts() {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [last, setLast] = useState({ id: null, at: null, tweetId: null });

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [postingId, setPostingId] = useState(null);

  const lastTweetUrl = useMemo(() => {
    if (!last.tweetId) return null;
    return `https://x.com/i/web/status/${last.tweetId}`;
  }, [last.tweetId]);

  const fetchPosts = async () => {
    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const headers = await authHeader();
      const res = await fetch(`${API_BASE}/admin/x/posts`, { method: "GET", headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed: ${res.status}`);
      }
      const data = await res.json();
      setPosts(data.posts || []);
      setLast({
        id: data.lastId || null,
        at: data.lastAt || null,
        tweetId: data.lastTweetId || null,
      });
    } catch (err) {
      console.error("Fetch X posts error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const postNow = async (postId) => {
    setError("");
    setSuccessMsg("");
    setPostingId(postId);
    try {
      const headers = await authHeader();
      const res = await fetch(`${API_BASE}/admin/x/posts`, {
        method: "POST",
        headers,
        body: JSON.stringify({ postId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed: ${res.status}`);
      }
      const data = await res.json();
      setSuccessMsg("Posted to X ✅");
      setLast((prev) => ({
        ...prev,
        id: data.postId || postId,
        at: Date.now(),
        tweetId: data.tweetId || prev.tweetId,
      }));
    } catch (err) {
      console.error("PostNow error:", err);
      setError(err.message);
    } finally {
      setPostingId(null);
    }
  };

  const postRandom = async () => {
    setError("");
    setSuccessMsg("");
    setPostingId("random");
    try {
      const headers = await authHeader();
      const res = await fetch(`${API_BASE}/admin/x/posts/random`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed: ${res.status}`);
      }
      const data = await res.json();
      setSuccessMsg(`Posted random ✅ (${data.postId})`);
      setLast({
        id: data.postId || null,
        at: Date.now(),
        tweetId: data.tweetId || null,
      });
    } catch (err) {
      console.error("PostRandom error:", err);
      setError(err.message);
    } finally {
      setPostingId(null);
    }
  };

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">𝕏</div>
            <div>
              <h3 className="admin-page-title">X Business Posts</h3>
              <div className="admin-page-subtitle">
                Press a button to post an evergreen brand statement from @littlepawsplace.
              </div>
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={fetchPosts} disabled={loading}>
              Refresh
            </Button>
            <Button variant="primary" size="sm" onClick={postRandom} disabled={!!postingId}>
              {postingId === "random" ? "Posting…" : "Post Random"}
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {successMsg && <Alert variant="success">{successMsg}</Alert>}

        <div className="admin-panel mb-3">
          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
            <div><strong>Last Post ID:</strong> {last.id || "—"}</div>
            <div><strong>Last Posted:</strong> {fmtDate(last.at)}</div>
            {lastTweetUrl && (
              <div>
                <strong>Last Tweet:</strong>{" "}
                <a href={lastTweetUrl} target="_blank" rel="noreferrer">
                  Open on X
                </a>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-4 text-center">
            <Spinner animation="border" /> Loading posts…
          </div>
        ) : (
          <div className="d-grid gap-3">
            {posts.map((p) => (
              <Card key={p.id} className="admin-panel">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      <div className="text-muted" style={{ fontSize: "0.85rem" }}>{p.id}</div>
                      <div style={{ fontSize: "1rem" }}>{p.text}</div>
                      <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                        {(p.text || "").length}/280
                      </div>
                    </div>
                    <Button
                      variant="success"
                      onClick={() => postNow(p.id)}
                      disabled={!!postingId}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {postingId === p.id ? "Posting…" : "Post Now"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}
